// THE EMAILED SIGN-OFF LINK (docs SIGNOFF-SPEC S4 / S5). No BidLight login: the token
// is the key. The building opens as it stood at the snapshot - the generated isometric by
// level, area and system (owner 2026-11-05), coloured by the sign-off of that moment, with
// what this request covers picked out and everything else pale - and the popup over it:
// Sign off, Request changes, or Look around first (the popup hides until Sign off in the
// header). An expired, used or withdrawn link says so instead.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Input, Result, Spin } from "antd";
import { StarFilled } from "@ant-design/icons";
import moment from "moment";
import { askNewLink, getSignoffLink, requestChangesLink, signOffLink } from "api/signoff";
import { RING, stateAt } from "./signoffState";
import SignoffIsometric from "./SignoffIsometric";
import { levelsFrom } from "./isoModel";
import "./signoff.scss";

const STAR = { ownerGc: "owner/GC", tenant: "contractor", partner: "trade partner" };

function left(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d ? `${d} d ${h} h` : `${h} h ${Math.floor((ms % 3600000) / 60000)} min`;
}

export default function SignoffLinkPage({ token }) {
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");
  const [popup, setPopup] = useState(true);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState("");
  const [changes, setChanges] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    getSignoffLink(token).then(setPage).catch((e) => setErr(e.message));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const states = useMemo(() => (page && page.rings ? stateAt(page.rings, page.request.snapshotAt) : new Map()), [page]);
  // the levels of the drawing, and the areas the request's own targets name
  const levels = useMemo(() => levelsFrom((page && page.tiles && page.tiles.tiles) || [], (page && page.levels) || {}), [page]);
  const scope = useMemo(() => {
    const ts = (page && page.request && page.request.targets) || [];
    return {
      levelKeys: ts.filter((t) => t.kind === "level").map((t) => t.levelKey).filter(Boolean),
      areaIds: ts.map((t) => t.areaSetId).filter(Boolean).map(String),
      systems: ts.filter((t) => t.kind === "system").map((t) => t.system).filter(Boolean),
      only: true,
    };
  }, [page]);

  const signOff = async () => {
    setBusy("sign");
    try {
      const r = await signOffLink(token);
      setDone({ kind: "signed", results: r.results || [] });
    } catch (e) { setErr(e.message); }
    setBusy("");
  };
  const sendChanges = async () => {
    setBusy("changes");
    try {
      const r = await requestChangesLink(token, changes);
      setDone({ kind: "changes", added: !!(r && r.added) });
    } catch (e) { setErr(e.message); }
    setBusy("");
  };
  const renew = async () => {
    setBusy("renew");
    try { await askNewLink(token); setDone({ kind: "renew" }); } catch (e) { setErr(e.message); }
    setBusy("");
  };

  if (!page && !err) return <div className="signoff-link-page"><Spin className="m-5" /></div>;
  const header = (
    <div className="signoff-link-head">
      <b className="brand">BidLight</b>
      <span className="signoff-muted">{page && page.project ? page.project.title : ""}</span>
      <span className="models-spacer" />
      {page && page.request && page.state === "open" ? (
        <>
          <span className="signoff-grid-chip">Snapshot · {moment(page.request.snapshotAt).format("MMM D, YYYY")}{page.request.exportVersion ? ` · export ${page.request.exportVersion}` : ""}</span>
          <span className="signoff-grid-chip is-warn">Link expires in {left(page.expiresAt)}</span>
          {!popup && !done ? <Button size="small" type="primary" style={{ background: RING[page.grant], borderColor: RING[page.grant] }} onClick={() => setPopup(true)}><StarFilled /> Sign off</Button> : null}
        </>
      ) : null}
    </div>
  );

  if (done) {
    return (
      <div className="signoff-link-page">
        {header}
        {done.kind === "signed" ? (
          <Result status="success" title="Signed off - thank you" subTitle={`Your sign-off of ${page.request.phase} is recorded with your name, the time and the snapshot.`}
                  extra={(done.results || []).filter((r) => r.message).map((r, i) => <Alert key={i} type="info" showIcon message={r.name ? `${r.name}: ${r.message}` : r.message} className="mb-2" />)} />
        ) : done.kind === "changes" ? (
          <Result status="info" title="Changes requested"
                  subTitle={done.added
                    ? "Your comment was added to the issue opened for your earlier changes. Your link stays open for when the changes are made."
                    : "The requester has your comment, and an issue was opened for it. Your link stays open for when the changes are made."} />
        ) : (
          <Result status="info" title="New link asked for" subTitle="The requester has been told; a new link will come by email." />
        )}
      </div>
    );
  }

  if (err && !page) return <div className="signoff-link-page">{header}<Result status="error" title="This link could not be opened" subTitle={err} /></div>;

  if (page.state !== "open") {
    const s = page.state;
    return (
      <div className="signoff-link-page" data-testid="signoff-link-state">
        {header}
        {s === "expired" ? (
          <Result status="warning" title="This sign-off link has expired" subTitle={`It expired ${moment(page.expiresAt).format("MMM D, h:mm A")}.`}
                  extra={page.renewAskedAt ? <span className="signoff-muted">A new link was asked for {moment(page.renewAskedAt).fromNow()}.</span> : <Button type="primary" loading={busy === "renew"} onClick={renew}>Ask for a new link</Button>} />
        ) : s === "used" ? (
          <Result status="success" title="Already used" subTitle={page.signedBy ? `Signed off by ${page.signedBy} on ${moment(page.usedAt).format("MMM D, YYYY h:mm A")}.` : `This link was used on ${moment(page.usedAt).format("MMM D, YYYY h:mm A")}.`} />
        ) : s === "closed" ? (
          <Result status="success" title="Already signed off" subTitle={`${page.closedBy || "Another owner/GC recipient"} signed this off for the owner/GC${page.closedAt ? ` on ${moment(page.closedAt).format("MMM D, YYYY h:mm A")}` : ""}. Nothing more is needed from you.`} />
        ) : s === "revoked" ? (
          <Result status="info" title="This link was withdrawn" subTitle="The requester withdrew it. If you still need to sign off, ask them for a new one." />
        ) : (
          <Result status="404" title="This link is not known" subTitle="Check that the whole link from the email was opened." />
        )}
      </div>
    );
  }

  const r = page.request;
  return (
    <div className="signoff-link-page">
      {header}
      <div className="signoff-link-body">
        <div className="signoff-link-tools">
          <span className="signoff-grid-chip">Showing: {r.targets.map((t) => t.label).join(", ")}</span>
          <span className="signoff-grid-chip" data-testid="grid-span">{(page.gridLines || []).length ? `Grid lines ${page.gridSpan || "-"}` : "No building grid in this model"}</span>
        </div>
        {page.tiles && levels.length ? (
          <SignoffIsometric tiles={page.tiles} levels={levels} areas={page.areas || {}} manifest={null} states={states}
                            scope={scope} focusKeys={page.focus} height={window.innerHeight - 160} />
        ) : <Alert className="m-4" type="info" showIcon message="The drawing is not available for this project yet; what is being signed off is listed above." />}
        {popup ? (
          <div className="signoff-popup-veil">
            <div className="signoff-popup" role="dialog" aria-label="Sign off">
              <small className="signoff-muted">Sign-off request from {r.createdByName || "the project team"}</small>
              <h3>{page.recipient.name || "Hello"}, please click <b>Sign off</b> to sign off on this phase of the model.</h3>
              <div><b>Phase:</b> {r.phase}</div>
              <div><b>Model as of:</b> {moment(r.snapshotAt).format("MMM D, YYYY")}{r.revision ? ` · revision #${r.revision}` : ""}{r.exportVersion ? ` · export ${r.exportVersion}` : ""} · {r.tiles} tiles · {r.elements.toLocaleString()} elements</div>
              {r.agreedAt ? <div><b>Agreed sign-off date:</b> {moment.utc(r.agreedAt).format("MMM D, YYYY")}</div> : null}
              {r.requiredBy ? <div><b>Sign off by:</b> {moment.utc(r.requiredBy).format("MMM D, YYYY")}</div> : null}
              <div><b>You sign as:</b> <StarFilled style={{ color: RING[page.grant] }} /> {STAR[page.grant]}{page.recipient.role ? ` · ${page.recipient.role}` : ""}{page.recipient.company ? ` (${page.recipient.company})` : ""}
                {page.grant === "tenant" ? ` · sets the gold star on ${r.activities} schedule activit${r.activities === 1 ? "y" : "ies"}` : page.grant === "partner" ? " · a silver star on the timeline" : " · sets the blue star"}</div>
              {r.message ? <p className="signoff-popup-msg">{r.message}</p> : null}
              {err ? <Alert type="error" showIcon message={err} className="mt-2" /> : null}
              {changes !== null ? (
                <>
                  <Input.TextArea className="mt-3" rows={3} autoFocus placeholder="What needs to change before you can sign off?" value={changes} onChange={(e) => setChanges(e.target.value)} />
                  <div className="signoff-popup-actions">
                    <Button onClick={() => setChanges(null)}>Back</Button>
                    <Button type="primary" loading={busy === "changes"} disabled={!changes.trim()} onClick={sendChanges}>Send changes</Button>
                  </div>
                </>
              ) : (
                <>
                  <Checkbox className="mt-3" checked={ok} onChange={(e) => setOk(e.target.checked)}>I reviewed the model shown and approve this phase.</Checkbox>
                  <div className="signoff-popup-actions">
                    <Button onClick={() => setPopup(false)}>Look around first</Button>
                    <Button onClick={() => setChanges("")}>Request changes</Button>
                    <Button type="primary" disabled={!ok} loading={busy === "sign"} onClick={signOff} data-testid="signoff-confirm"
                            style={ok ? { background: RING[page.grant], borderColor: RING[page.grant] } : undefined}><StarFilled /> Sign off</Button>
                  </div>
                </>
              )}
              <small className="signoff-muted d-block mt-2">Your name, the time and this snapshot are recorded with the sign-off.</small>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
